import React, { useState, useRef, useEffect } from 'react';

// Generic imports
import _ from 'lodash';
import { useDrop } from 'react-dnd';

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { faClock, faCalendar, faRoute, faBuilding, faCircleDollarToSlot } from '@fortawesome/pro-light-svg-icons';

// MUI imports
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { red, grey } from '@mui/material/colors';
import { useTheme, alpha } from '@mui/material/styles';

// Local imports
import SpecialInputField from './SpecialInputField.js';
import CurrencyList from 'currency-list';

function getRatesForSection({ buyingRates, sectionOffer }) {
    const fieldBuyingRates = _.flatten(sectionOffer.fields.map(f => f.buyingRates || [])).map(r => r.rateId);
    return (buyingRates || []).filter(br => fieldBuyingRates.includes(br.id));
}

function SectionHeader(props) {
    const { offer, section, offerIndex, sectionIndex, locked, setModal, modal, products, exchangeRates, 
        buyingRates, transportationMethod, onAddSection, offers, setOffers, status, setOfferDisplayCurrency, organization } = props;

    const theme = useTheme();

    const [ dropOver, setDropOver ] = useState(null);
    const [ isEditing, setIsEditing ] = useState(false);
    const [ editValue, setEditValue ] = useState(section.title || '');
    const [ isEditingDescription, setIsEditingDescription ] = useState(false);
    const [ editingDescriptionValue, setEditingDescriptionValue ] = useState('');

    useEffect(() => {
        if (!isEditing) {
            setEditValue(section.title || '');
        }
    }, [section.title, isEditing]);
    const productsRef = useRef();
    productsRef.current = products;

    const first = useRef();
    const sectionRef = useRef();
    sectionRef.current = section;
    const setDropOverRef = useRef();
    setDropOverRef.current = setDropOver;
    const onAddSectionRef = useRef();
    onAddSectionRef.current = onAddSection;

    const [ , drop ] = useDrop(() => ({ 
        accept: ['RateCardSingleSection', 'RateCard'],
        hover: (item, monitor) => {
            if (first.current) return;
            first.current = true;
            setDropOverRef.current(monitor.canDrop(item, monitor) ? 'candrop' + (item.rate.type === 'tariff' ? '-tariff' : '') : 'cantdrop' + (item.rate.type === 'tariff' ? '-tariff' : ''));
            function isOver() {
                if (monitor.isOver()) {
                    setTimeout(isOver, 100);
                } else {
                    first.current = false;
                    setDropOverRef.current(null);
                }
            }
            setTimeout(isOver, 100);
        },
        drop: (item, monitor) => {
            if (item.section) {
                onAddSectionRef.current({rate: item.rate, section: item.section });
            } else {
                const _section = item.rate.offer.sections.find(s => s.title === sectionRef.current.title);
                onAddSectionRef.current({rate: item.rate, section: _section });
            }
        },
        canDrop: (item, monitor) => {
            return _.some(productsRef.current, p => {
                const _type = item.rate.product.type;
                const _type2 = p.type;

                if (_type !== _type2) return false;
                if ((item.rate.product.dangerous || false) !== (p.dangerous || false)) return false;
                if (!item.section && !item.rate.offer.sections.find(s => s.title === sectionRef.current.title)) return false;
                return true;
            });
        },
    }));
    
    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%', position: 'relative' }} ref={drop}>
            <Box sx={{ mb: 0.75, color: theme.palette.text.secondary, fontSize: '12px', fontWeight: 400 }}>
                Section {sectionIndex + 1} of {offer.sections.length}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.75 }}>
                {isEditing && !locked ? (
                    <TextField
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => {
                            if (setOffers && offers) {
                                const newOffers = _.cloneDeep(offers);
                                _.set(newOffers, `[${offerIndex}].sections[${sectionIndex}].title`, editValue);
                                setOffers(newOffers);
                            } else {
                                setModal({
                                    title: 'Set Section\'s Title',
                                    label: 'Title',
                                    value: editValue,
                                    path: `[${offerIndex}].sections[${sectionIndex}].title`,
                                    open: true,
                                });
                            }
                            setIsEditing(false);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.target.blur();
                            } else if (e.key === 'Escape') {
                                setEditValue(section.title || '');
                                setIsEditing(false);
                            }
                        }}
                        variant='standard'
                        sx={{ flex: 1, minWidth: '200px' }}
                    />
                ) : (
                    <>
                        <Box 
                            sx={{ 
                                color: theme.palette.text.primary,
                                fontSize: '16px',
                                fontWeight: 500,
                                cursor: locked ? 'default' : 'pointer',
                                '&:hover': locked ? {} : { textDecoration: 'underline' }
                            }}
                            onClick={() => {
                                if (!locked) {
                                    setEditValue(section.title || '');
                                    setIsEditing(true);
                                }
                            }}
                        >
                            {section.title || <i style={{ opacity: 0.6 }}>New Section</i>}
                        </Box>
                        {section.offers.length === 1 && (
                            <Box sx={{ marginLeft: '8px', display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ margin: '0px 8px 0px 0px' }}>
                                    <SpecialInputField
                                        icon={<FontAwesomeIcon icon={faClock} />}
                                        emptyStateText={'Transit Time'}
                                        title={'Set Transit Time'}
                                        label={'Transit Time'}
                                        path={`[${offerIndex}].sections[${sectionIndex}].offers[0].transitTime`}
                                        inputType='transit-time'
                                        options={getRatesForSection({ buyingRates, sectionOffer: section.offers[0] }).map(r => r.transportationMethod === 'sea' ? r.offer.transitTime * 24 : r.offer.transitTime)}
                                        optionsDescription={'Add a transit time to this section. Cost rates transit times:'}
                                        modal={modal}
                                        setModal={setModal}
                                        offers={offers}
                                        setOffers={setOffers}
                                        disabled={(!!status && status !== 'draft') || locked}
                                    />
                                </Box>

                                <Box sx={{ margin: '0px 8px 0px 0px' }}>
                                    <SpecialInputField
                                        icon={<FontAwesomeIcon icon={faCalendar} />}
                                        emptyStateText={'Validity'}
                                        title={'Set Validity'}
                                        label={'Validity'}
                                        path={`[${offerIndex}].sections[${sectionIndex}].offers[0]`}
                                        inputType='validity-range'
                                        options={getRatesForSection({ buyingRates, sectionOffer: section.offers[0] }).map(r => r.offer.validUntil)}
                                        optionsDescription={'Let your customers know from and until what date this section is valid. Cost rates validity:'}
                                        modal={modal}
                                        setModal={setModal}
                                        offers={offers}
                                        setOffers={setOffers}
                                        disabled={(!!status && status !== 'draft') || locked}
                                    />
                                </Box>

                                <Box sx={{ margin: '0px 8px 0px 0px' }}>
                                    <SpecialInputField
                                        icon={<FontAwesomeIcon icon={faRoute} />}
                                        emptyStateText={'Route'}
                                        title={'Set Route'}
                                        label={'Route'}
                                        path={`[${offerIndex}].sections[${sectionIndex}].offers[0].routes`}
                                        inputType='routes'
                                        modal={modal}
                                        setModal={setModal}
                                        offers={offers}
                                        setOffers={setOffers}
                                        disabled={(!!status && status !== 'draft') || locked}
                                    />
                                </Box>

                                <Box sx={{ margin: '0px 8px 0px 0px' }}>
                                    <SpecialInputField
                                        icon={<FontAwesomeIcon icon={faBuilding} />}
                                        emptyStateText={'Carrier'}
                                        title={'Set Carrier'}
                                        label={'Carrier'}
                                        path={`[${offerIndex}].sections[${sectionIndex}].offers[0].carrier`}
                                        inputType='carrier'
                                        optionsDescription={'Add a carrier to the entire offer.'}
                                        modal={modal}
                                        setModal={setModal}
                                        offers={offers}
                                        setOffers={setOffers}
                                        disabled={(!!status && status !== 'draft') || locked}
                                    />
                                </Box>

                                <Box sx={{ margin: '0px 8px 0px 0px' }}>
                                    <Tooltip title={
                                        (section.displayCurrency?.convert || section.displayCurrency?.length === 3)
                                            ? CurrencyList.getAll('en_US')[section.displayCurrency?.to || section.displayCurrency].name
                                            : 'Display currency'
                                    }>
                                        <span>
                                            <Button
                                                variant='text'
                                                sx={{
                                                    minWidth: '32px',
                                                    width: '32px',
                                                    height: '32px',
                                                    padding: '0px',
                                                    borderRadius: '6px',
                                                    border: 'none',
                                                    color: (section.displayCurrency?.convert || section.displayCurrency?.length === 3) ? theme.palette.primary.main : theme.palette.text.secondary,
                                                    '&:hover': {
                                                        backgroundColor: (section.displayCurrency?.convert || section.displayCurrency?.length === 3) ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.text.secondary, 0.08),
                                                        border: 'none',
                                                        color: (section.displayCurrency?.convert || section.displayCurrency?.length === 3) ? theme.palette.primary.main : theme.palette.text.secondary
                                                    },
                                                    '&.Mui-disabled': {
                                                        color: theme.palette.action.disabled,
                                                        border: 'none'
                                                    },
                                                    '&:focus': {
                                                        outline: 'none',
                                                        border: 'none'
                                                    }
                                                }}
                                                onClick={() => setOfferDisplayCurrency && setOfferDisplayCurrency({ offer, section })}
                                            >
                                                <FontAwesomeIcon icon={faCircleDollarToSlot} style={{ fontSize: '13px' }} />
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </Box>
                        )}
                    </>
                )}
                {section.hideBreakdown && (
                    <Typography variant='caption' sx={{ flex: 1, color: red[500], fontStyle: 'italic', textAlign: 'end' }}>
                        (Breakdown hidden)
                    </Typography>
                )}
            </Box>
            {(!locked || section.description) && (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.75 }}>
                    {isEditingDescription && !locked ? (
                        <TextField
                            autoFocus
                            value={editingDescriptionValue}
                            onChange={(e) => setEditingDescriptionValue(e.target.value)}
                            onBlur={() => {
                                if (setOffers && offers) {
                                    const newOffers = _.cloneDeep(offers);
                                    _.set(newOffers, `[${offerIndex}].sections[${sectionIndex}].description`, editingDescriptionValue);
                                    setOffers(newOffers);
                                } else {
                                    setModal({
                                        title: 'Set Section\'s Description',
                                        label: 'Description',
                                        value: editingDescriptionValue,
                                        path: `[${offerIndex}].sections[${sectionIndex}].description`,
                                        open: true,
                                    });
                                }
                                setIsEditingDescription(false);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.target.blur();
                                } else if (e.key === 'Escape') {
                                    setEditingDescriptionValue(section.description || '');
                                    setIsEditingDescription(false);
                                }
                            }}
                            variant='standard'
                            placeholder='You can add a description to this section here.'
                            sx={{ flex: 1, minWidth: '200px' }}
                        />
                    ) : (
                        <Box
                            sx={{
                                fontWeight: 400,
                                fontSize: '13px',
                                color: theme.palette.text.secondary,
                                cursor: locked ? 'default' : 'pointer',
                                '&:hover': locked ? {} : { textDecoration: 'underline' }
                            }}
                            onClick={() => {
                                if (!locked) {
                                    setEditingDescriptionValue(section.description || '');
                                    setIsEditingDescription(true);
                                }
                            }}
                        >
                            {section.description || <i style={{ opacity: 0.6 }}>You can add a description to this section here.</i>}
                        </Box>
                    )}
                </Box>
            )}
            {dropOver === 'candrop' && (
                <Box sx={{ position: 'absolute', background: theme.palette.border1, opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Drop your rate here to update this section with its fields.
                </Box>
            )}
            {dropOver === 'candrop-tariff' && (
                <Box sx={{ position: 'absolute', background: theme.palette.border1, opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Drop your tariff here to update this section with its fields.
                </Box>
            )}
            {dropOver === 'cantdrop' && (
                <Box sx={{ position: 'absolute', background: red[500], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Rate is for a product not included in the opportunity or does not include '{section.title}'.
                </Box>
            )}
            {dropOver === 'cantdrop-tariff' && (
                <Box sx={{ position: 'absolute', background: red[500], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Tariff is for a product not included in the opportunity or does not include '{section.title}'.
                </Box>
            )}
        </Box>
    );
}

export default SectionHeader;
